import { createDemo } from '@mui/internal-core-docs/utils/createDemo';
import CreateCssVarsProvider from './CreateCssVarsProvider';
import ClientProvider from './client';
export default createDemo(import.meta.url, CreateCssVarsProvider, { ClientProvider, precompute: {
  "Default": {
    "url": "file:///opt/build/repo/docs/data/system/experimental-api/css-theme-variables/demos/create-css-vars-provider/CreateCssVarsProvider.tsx",
    "fileName": "CreateCssVarsProvider.tsx",
    "language": "tsx",
    "source": {
      "type": "root",
      "children": [
        {
          "type": "element",
          "tagName": "span",
          "properties": {
            "className": [
              "frame"
            ]
          },
          "children": [
            {
              "type": "text",
              "value": "import * as React from 'react';\nimport {\n  unstable_createCssVarsProvider as createCssVarsProvider,\n  unstable_prepareCssVars as prepareCssVars,\n  styled,\n} from '@mui/system';\n\ntype Theme = {\n  colorSchemes: {\n    light: typeof lightColorScheme;\n    dark: typeof lightColorScheme;\n  };\n  palette: {\n    colorScheme: 'light' | 'dark';\n  } & (typeof lightColorScheme)['palette'];\n} & ReturnType<typeof prepareCssVars>;\n\nconst lightColorScheme = {\n  palette: {\n    mode: 'light',\n    primary: {\n      default: '#3990FF',\n      dark: '#02367D',\n    },\n    text: {\n      default: '#111111',\n    },\n    // ... other colors\n  },\n};\n\nconst darkColorScheme = {\n  palette: {\n    mode: 'dark',\n    primary: {\n      default: '#265D97',\n      dark: '#132F4C',\n      main: '#5090D3',\n    },\n    text: {\n      default: '#ffffff',\n    },\n    // ... other colors\n  },\n};\n\nfunction extendTheme({ cssVarPrefix = 'system-demo' } = {}) {\n  const colorSchemeSelector = 'data-system-demo-color-scheme';\n  const { vars: themeVars, ...params } = prepareCssVars(\n    {\n      colorSchemes: {\n        light: lightColorScheme,\n        dark: darkColorScheme,\n      },\n    },\n    {\n      prefix: cssVarPrefix,\n      colorSchemeSelector,\n    },\n  );\n  const theme: Theme & { colorSchemeSelector: string } = {\n    colorSchemeSelector,\n    colorSchemes: {\n      light: lightColorScheme,\n      dark: darkColorScheme,\n    },\n    // ... any other objects independent of color-scheme,\n    // like fontSizes, spacing etc\n    vars: themeVars,\n    palette: {\n      ...lightColorScheme.palette,\n      colorScheme: 'light',\n    },\n    ...params,\n  };\n\n  return theme;\n}\n\nconst myCustomDefaultTheme = extendTheme();\n\nconst { CssVarsProvider, useColorScheme } = createCssVarsProvider({\n  theme: myCustomDefaultTheme,\n  modeStorageKey: 'system-demo-mode',\n  defaultColorScheme: {\n    light: 'light',\n    dark: 'dark',\n  },\n});\n\nconst Button = styled('button')<{ theme?: Theme }>(({ theme }) => ({\n  backgroundColor: theme.vars.palette.primary.default,\n  border: `1px solid ${theme.vars.palette.primary.dark}`,\n  color: theme.vars.palette.text.default,\n  padding: 10,\n  borderRadius: 5,\n  fontWeight: 'bold',\n}));\n\nconst WrapperDiv = styled('div')<{ theme?: Theme }>(({ theme }) => ({\n  width: '100%',\n  minHeight: 100,\n  padding: 20,\n  color: theme.vars.palette.text.default,\n  backgroundColor: '#fff',\n  '[data-system-demo-color-scheme=\"dark\"] &': {\n    backgroundColor: '#111',\n  },\n}));\n\nfunction App() {\n  // changes specific to this demo.\n  const [shouldRender, setShouldRender] = React.useState(false);\n  const { setMode, mode } = useColorScheme();\n  const toggleMode = () => {\n    setMode(mode === 'dark' ? 'light' : 'dark');\n  };\n\n  // to avoid hydration error in demo. unrelated to the actual implementation\n  React.useEffect(() => {\n"
            }
          ]
        },
        {
          "type": "element",
          "tagName": "span",
          "properties": {
            "className": [
              "frame"
            ]
          },
          "children": [
            {
              "type": "text",
              "value": "    setShouldRender(true);\n  }, []);\n\n  if (!shouldRender) {\n    return null;\n  }\n\n  return (\n    <WrapperDiv className=\"App\">\n      <div className=\"card\">\n        <h2>Current mode: {mode}</h2>\n        <Button type=\"button\" onClick={toggleMode}>\n          Toggle Mode\n        </Button>\n      </div>\n    </WrapperDiv>\n  );\n}\n\nexport default function CreateCssVarsProvider() {\n  return (\n"
            }
          ]
        },
        {
          "type": "element",
          "tagName": "span",
          "properties": {
            "dataFrameType": "focus",
            "dataFrameIndent": 2,
            "dataFrameFocusTarget": "",
            "className": [
              "frame"
            ]
          },
          "children": [
            {
              "type": "text",
              "value": "    <CssVarsProvider>\n      <App />\n    </CssVarsProvider>\n"
            }
          ]
        },
        {
          "type": "element",
          "tagName": "span",
          "properties": {
            "className": [
              "frame"
            ]
          },
          "children": [
            {
              "type": "text",
              "value": "  );\n}\n"
            }
          ]
        }
      ],
      "data": {
        "totalLines": 146,
        "focusedLines": 3,
        "collapsible": true
      }
    },
    "fallback": [
      [
        "span",
        "frame",
        "import * as React from 'react';\nimport {\n  unstable_createCssVarsProvider as createCssVarsProvider,\n  unstable_prepareCssVars as prepareCssVars,\n  styled,\n} from '@mui/system';\n\ntype Theme = {\n  colorSchemes: {\n    light: typeof lightColorScheme;\n    dark: typeof lightColorScheme;\n  };\n  palette: {\n    colorScheme: 'light' | 'dark';\n  } & (typeof lightColorScheme)['palette'];\n} & ReturnType<typeof prepareCssVars>;\n\nconst lightColorScheme = {\n  palette: {\n    mode: 'light',\n    primary: {\n      default: '#3990FF',\n      dark: '#02367D',\n    },\n    text: {\n      default: '#111111',\n    },\n    // ... other colors\n  },\n};\n\nconst darkColorScheme = {\n  palette: {\n    mode: 'dark',\n    primary: {\n      default: '#265D97',\n      dark: '#132F4C',\n      main: '#5090D3',\n    },\n    text: {\n      default: '#ffffff',\n    },\n    // ... other colors\n  },\n};\n\nfunction extendTheme({ cssVarPrefix = 'system-demo' } = {}) {\n  const colorSchemeSelector = 'data-system-demo-color-scheme';\n  const { vars: themeVars, ...params } = prepareCssVars(\n    {\n      colorSchemes: {\n        light: lightColorScheme,\n        dark: darkColorScheme,\n      },\n    },\n    {\n      prefix: cssVarPrefix,\n      colorSchemeSelector,\n    },\n  );\n  const theme: Theme & { colorSchemeSelector: string } = {\n    colorSchemeSelector,\n    colorSchemes: {\n      light: lightColorScheme,\n      dark: darkColorScheme,\n    },\n    // ... any other objects independent of color-scheme,\n    // like fontSizes, spacing etc\n    vars: themeVars,\n    palette: {\n      ...lightColorScheme.palette,\n      colorScheme: 'light',\n    },\n    ...params,\n  };\n\n  return theme;\n}\n\nconst myCustomDefaultTheme = extendTheme();\n\nconst { CssVarsProvider, useColorScheme } = createCssVarsProvider({\n  theme: myCustomDefaultTheme,\n  modeStorageKey: 'system-demo-mode',\n  defaultColorScheme: {\n    light: 'light',\n    dark: 'dark',\n  },\n});\n\nconst Button = styled('button')<{ theme?: Theme }>(({ theme }) => ({\n  backgroundColor: theme.vars.palette.primary.default,\n  border: `1px solid ${theme.vars.palette.primary.dark}`,\n  color: theme.vars.palette.text.default,\n  padding: 10,\n  borderRadius: 5,\n  fontWeight: 'bold',\n}));\n\nconst WrapperDiv = styled('div')<{ theme?: Theme }>(({ theme }) => ({\n  width: '100%',\n  minHeight: 100,\n  padding: 20,\n  color: theme.vars.palette.text.default,\n  backgroundColor: '#fff',\n  '[data-system-demo-color-scheme=\"dark\"] &': {\n    backgroundColor: '#111',\n  },\n}));\n\nfunction App() {\n  // changes specific to this demo.\n  const [shouldRender, setShouldRender] = React.useState(false);\n  const { setMode, mode } = useColorScheme();\n  const toggleMode = () => {\n    setMode(mode === 'dark' ? 'light' : 'dark');\n  };\n\n  // to avoid hydration error in demo. unrelated to the actual implementation\n  React.useEffect(() => {\n"
      ],
      [
        "span",
        "frame",
        "    setShouldRender(true);\n  }, []);\n\n  if (!shouldRender) {\n    return null;\n  }\n\n  return (\n    <WrapperDiv className=\"App\">\n      <div className=\"card\">\n        <h2>Current mode: {mode}</h2>\n        <Button type=\"button\" onClick={toggleMode}>\n          Toggle Mode\n        </Button>\n      </div>\n    </WrapperDiv>\n  );\n}\n\nexport default function CreateCssVarsProvider() {\n  return (\n"
      ],
      [
        "span",
        "frame",
        {
          "dataFrameType": "focus",
          "dataFrameIndent": 2,
          "dataFrameFocusTarget": ""
        },
        "    <CssVarsProvider>\n      <App />\n    </CssVarsProvider>\n"
      ],
      [
        "span",
        "frame",
        "  );\n}\n"
      ]
    ],
    "fallbackCritical": {
      "2": [
        "span",
        "frame",
        {
          "dataFrameType": "focus",
          "dataFrameIndent": 2,
          "dataFrameFocusTarget": ""
        },
        [
          [
            "span",
            "line",
            {
              "dataLn": 142
            },
            [
              "    <",
              [
                "span",
                "pl-c1 di-jsx",
                "CssVarsProvider"
              ],
              ">"
            ]
          ],
          "\n",
          [
            "span",
            "line",
            {
              "dataLn": 143
            },
            [
              "      <",
              [
                "span",
                "pl-c1 di-jsx",
                "App"
              ],
              " />"
            ]
          ],
          "\n",
          [
            "span",
            "line",
            {
              "dataLn": 144
            },
            [
              "    </",
              [
                "span",
                "pl-c1 di-jsx",
                "CssVarsProvider"
              ],
              ">"
            ]
          ],
          "\n"
        ]
      ]
    },
    "totalLines": 146,
    "focusedLines": 3,
    "collapsible": true,
    "externals": [
      "react",
      "@mui/system"
    ]
  }
}, loadPrecompute: () => import("./index.ts?mui-deferred-precompute").then((module) => module.default) });
